<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { eventBus } from '@/utils/eventBus';
import IconButton from '@/components/shared/IconButton.vue';

type MessageVariant = 'success' | 'error';

type StyleMap = {
  icon: string[];
  bgColor: string;
};

interface Props {
  title: string;
  variant?: MessageVariant;
  duration?: number;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'success',
  duration: 3000,
});

const emit = defineEmits<{ close: [] }>();

const showContent = ref<boolean>(false);
const showWrapper = ref<boolean>(true);
const timeoutId = ref<ReturnType<typeof setTimeout>>();

const STYLES: Record<MessageVariant, StyleMap> = {
  success: {
    bgColor: 'bg-darkOcean-600',
    icon: ['fas', 'check'],
  },
  error: {
    bgColor: 'bg-red-400',
    icon: ['fas', 'triangle-exclamation'],
  },
};

onMounted(() => {
  eventBus.on('triggerClose', close);
  showContent.value = true;

  if (props.duration) {
    timeoutId.value = setTimeout(() => {
      eventBus.emit('triggerClose');
    }, props.duration);
  }
});

onBeforeUnmount(() => {
  eventBus.off('triggerClose', close);
  if (timeoutId.value !== null) {
    clearTimeout(timeoutId.value);
  }
});

const close = () => {
  // Close wrapper overlay before animating the overlay itself down
  showContent.value = false;
  setTimeout(() => {
    showWrapper.value = false;
    eventBus.emit('triggerClose');
    emit('close');
  }, 400);
};

defineExpose({ close });
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-500 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-500 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showWrapper" class="fixed inset-0 z-999 bg-[rgba(0,0,0,0.5)]">
        <Transition
          enter-active-class="transition-transform duration-500 ease-out"
          enter-from-class="translate-y-full"
          enter-to-class="translate-y-0"
          leave-active-class="transition-transform duration-500 ease-in"
          leave-from-class="translate-y-0"
          leave-to-class="translate-y-full"
        >
          <div v-if="showContent" class="h-full flex flex-col justify-end">
            <div
              class="bg-neutral-100 rounded-[4rem] p-12 pb-34 shadow-small mx-10 mb-10"
            >
              <div class="pb-14">
                <IconButton :icon="['fas', 'times']" @click="close" />
              </div>

              <div class="flex items-center justify-center flex-col gap-6">
                <div
                  class="flex items-center justify-center rounded-full w-30 h-30"
                  :class="STYLES[variant].bgColor"
                >
                  <fa-icon
                    :icon="STYLES[variant].icon"
                    class="text-neutral-0 text-[3.5rem]"
                  />
                </div>
                <div class="flex flex-col gap-1 items-center">
                  <p class="h1000">
                    {{ title }}
                  </p>
                  <p v-if="variant === 'error'" class="b500">
                    {{ $t('global.error_suggestion') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
* {
  z-index: 999;
}
</style>
